import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  areaFromAddress,
  formatRating,
  primaryCuisine,
  restoInfoPath,
} from "../../utils/restoLink";

const FALLBACK = "/icons/icon-192.png";

const RestaurantCard = ({
  restaurant,
  variant = "editorial",
  selected = false,
  onSelect,
  to,
}) => {
  if (!restaurant) return null;
  const href = to || restoInfoPath(restaurant);
  const cuisine = primaryCuisine(restaurant.cuisine);
  const area = areaFromAddress(restaurant.address);
  const meta = [cuisine, area].filter(Boolean).join(" · ");
  const rating = formatRating(restaurant.rating);

  const media = (
    <Thumb
      src={restaurant.picture || FALLBACK}
      alt=""
      loading="lazy"
      decoding="async"
      onError={(e) => {
        e.currentTarget.src = FALLBACK;
      }}
    />
  );

  const body = (
    <Body>
      <Name>{restaurant.name}</Name>
      {meta ? <Meta>{meta}</Meta> : null}
      <Stats>
        {rating ? <Rating>★ {rating}</Rating> : null}
        {restaurant.price ? <Price>{restaurant.price}</Price> : null}
      </Stats>
    </Body>
  );

  if (onSelect) {
    return (
      <Card
        as="button"
        type="button"
        $variant={variant}
        $selected={selected}
        onClick={onSelect}
      >
        {media}
        {body}
      </Card>
    );
  }

  return (
    <Card as={Link} to={href} $variant={variant} $selected={selected}>
      {media}
      {body}
    </Card>
  );
};

export default RestaurantCard;

const Card = styled.a`
  display: flex;
  flex-direction: ${(p) => (p.$variant === "row" ? "row" : "column")};
  align-items: ${(p) => (p.$variant === "row" ? "center" : "stretch")};
  gap: ${(p) => (p.$variant === "row" ? "12px" : "10px")};
  text-decoration: none;
  color: inherit;

  img {
    width: ${(p) => (p.$variant === "row" ? "88px" : "100%")};
    height: ${(p) => (p.$variant === "row" ? "88px" : "auto")};
    aspect-ratio: ${(p) => (p.$variant === "row" ? "1" : "4 / 3")};
  }
  background: ${(p) =>
    p.$variant === "dark" ? "var(--surface-dark-2)" : "transparent"};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? "var(--color-yellow)"
        : p.$variant === "dark"
          ? "var(--border-on-dark)"
          : "transparent"};
  border-radius: var(--radius);
  padding: ${(p) => (p.$variant === "row" ? "8px" : "0")};
  text-align: left;
  min-width: ${(p) => (p.$variant === "scroll" ? "220px" : "0")};
  width: ${(p) => (p.$variant === "scroll" ? "220px" : "auto")};
  flex: ${(p) => (p.$variant === "scroll" ? "0 0 220px" : "1 1 auto")};
`;

const Thumb = styled.img`
  object-fit: cover;
  border-radius: var(--radius);
  background: var(--color-stone);
  display: block;
  flex-shrink: 0;
`;

const Body = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.h3`
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.2;
  margin: 0;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.p`
  margin: 0;
  font-size: 12px;
  color: inherit;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const Rating = styled.span`
  color: inherit;
`;

const Price = styled.span`
  opacity: 0.7;
  letter-spacing: 0.04em;
`;
